import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type ToastVariant = "default" | "success" | "error" | "warning" | "info";

export interface ToastOptions {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface Toast extends Required<ToastOptions> {
  id: string;
}

interface ToastContextValue {
  show: (opts: ToastOptions | string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToasterProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((opts: ToastOptions | string) => {
    const base: Toast = {
      id: Math.random().toString(36).slice(2),
      title: typeof opts === "string" ? opts : opts.title || "",
      description: typeof opts === "string" ? "" : opts.description || "",
      variant: typeof opts === "string" ? "default" : opts.variant || "default",
      durationMs: typeof opts === "string" ? 4000 : opts.durationMs || 4000,
    };
    setToasts(prev => [...prev, base]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== base.id));
    }, base.durationMs);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed z-50 bottom-4 right-4 flex flex-col gap-2 w-[min(90vw,360px)]">
        {toasts.map(t => (
          <div
            key={t.id}
            className={
              "rounded-md border p-3 shadow bg-white/95 backdrop-blur flex flex-col gap-1 " +
              (t.variant === "success" ? "border-green-300" :
               t.variant === "error" ? "border-red-300" :
               t.variant === "warning" ? "border-yellow-300" :
               t.variant === "info" ? "border-blue-300" : "border-gray-200")
            }
          >
            {t.title && <div className="text-sm font-medium">{t.title}</div>}
            {t.description && <div className="text-xs text-gray-600">{t.description}</div>}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToasterProvider");
  return ctx;
}

